
"use client";

import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { format, isToday, isTomorrow } from "date-fns";
import {
  generateTimeSlots,
  fetchBookedSlots,
  getBusinessSettings,
  getBusinessIdFromProviderId,
  getBusinessTimezone
} from '@/utils/availabilityCore';
import { FLOW_SPRING, FLOW_DEFAULTS } from '@/components/booking-flow/themes';
import { SectionLabel } from '@/components/booking-flow/FlowScaffold';

/**
 * DateTimeSelectionStep — step 2 of the booking flow.
 *
 * Visuals matched against the Claude Design prototype (screens.jsx
 * MonthCalendar / TimeChip / SectionLabel in design-imports/), driven
 * by the app's real availability engine.
 */

interface DateTimeSelectionStepProps {
  serviceDuration: number;
  selectedDateTime: { date: Date; time: string } | null;
  onSelectDateTime: (dateTime: { date: Date; time: string } | null) => void;
  businessId?: string;
  providerId?: string;
  /** Notifies date-only picks (time not chosen yet) so the CTA sub-line can react */
  onDateChange?: (date: Date | undefined) => void;
}

const CARD_SHADOW = FLOW_DEFAULTS.cardShadow;
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

// ─── MonthCalendar ───────────────────────────────────────────────

function CalChevron({ dir, disabled, onClick }: {
  dir: 'left' | 'right';
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={dir === 'left' ? 'Previous month' : 'Next month'}
      style={{
        width: 32,
        height: 32,
        borderRadius: 16,
        border: '0.5px solid var(--f-hairline, rgba(28,18,12,0.10))',
        background: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled ? 'default' : 'pointer',
        padding: 0,
        opacity: disabled ? 0.3 : 1,
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ transform: dir === 'right' ? 'scaleX(-1)' : 'none' }}>
        <path d="M7.5 2L3.5 6L7.5 10" stroke="var(--f-ink, #1c160f)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

function DayCell({ day, view, selectedDate, onPick }: {
  day: number | null;
  view: { y: number; m: number };
  selectedDate: Date | undefined;
  onPick: (d: Date) => void;
}) {
  if (day == null) return <div style={{ height: 42 }} />;
  const today = startOfToday();
  const d = new Date(view.y, view.m, day);
  d.setHours(0, 0, 0, 0);
  const past = d < today;
  const selected = selectedDate != null && sameDay(d, selectedDate);
  const isTodayCell = sameDay(d, today);

  return (
    <button
      type="button"
      onClick={() => !past && onPick(d)}
      disabled={past}
      data-cal-day={past ? undefined : `${view.y}-${view.m + 1}-${day}`}
      style={{
        height: 42,
        borderRadius: 12,
        border: 'none',
        background: selected ? 'var(--f-sel-bg, #1c160f)' : 'transparent',
        color: selected
          ? 'var(--f-sel-fg, #F5EDE0)'
          : past
            ? 'var(--f-ink-faint, rgba(28,18,12,0.40))'
            : 'var(--f-ink, #1c160f)',
        opacity: past ? 0.3 : 1,
        cursor: past ? 'default' : 'pointer',
        fontFamily: 'inherit',
        fontSize: 14.5,
        fontVariantNumeric: 'tabular-nums',
        position: 'relative',
        transition: `background 220ms ${FLOW_SPRING}, color 220ms ${FLOW_SPRING}`,
        WebkitTapHighlightColor: 'transparent',
        boxShadow: selected ? '0 6px 14px rgba(28,18,12,0.18)' : 'none',
        padding: 0,
      }}
    >
      {day}
      {isTodayCell && !selected && (
        <span style={{
          position: 'absolute',
          bottom: 6,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 4,
          height: 4,
          borderRadius: '50%',
          background: 'var(--f-accent, #1c160f)',
        }} />
      )}
    </button>
  );
}

function MonthCalendar({ selectedDate, onPick }: {
  selectedDate: Date | undefined;
  onPick: (d: Date) => void;
}) {
  const today = startOfToday();
  const init = selectedDate ?? today;
  const [view, setView] = useState({ y: init.getFullYear(), m: init.getMonth() });

  const firstDow = new Date(view.y, view.m, 1).getDay();
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const cells: Array<number | null> = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const atMinMonth = view.y === today.getFullYear() && view.m <= today.getMonth();
  const go = (delta: number) => setView((v) => {
    let m = v.m + delta, y = v.y;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    return { y, m };
  });

  return (
    <div style={{
      background: 'var(--f-surface, #FBF8F2)',
      border: '0.5px solid var(--f-hairline, rgba(28,18,12,0.10))',
      borderRadius: 18,
      padding: '14px 14px 12px',
      boxShadow: CARD_SHADOW,
    }}>
      {/* header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 12,
        padding: '0 2px',
      }}>
        <CalChevron dir="left" disabled={atMinMonth} onClick={() => !atMinMonth && go(-1)} />
        <div style={{
          fontFamily: 'var(--f-display, "Inter Tight", system-ui, sans-serif)',
          fontStyle: 'var(--f-display-style, normal)' as React.CSSProperties['fontStyle'],
          fontWeight: 'var(--f-display-weight, 600)' as React.CSSProperties['fontWeight'],
          fontSize: 19,
          letterSpacing: '-0.01em',
          color: 'var(--f-ink, #1c160f)',
        }}>{MONTHS_LONG[view.m]} {view.y}</div>
        <CalChevron dir="right" onClick={() => go(1)} />
      </div>

      {/* weekday labels */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 4 }}>
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((w, i) => (
          <div key={i} style={{
            textAlign: 'center',
            fontSize: 10,
            fontWeight: 600,
            color: 'var(--f-ink-faint, rgba(28,18,12,0.40))',
            letterSpacing: '0.04em',
            padding: '3px 0',
          }}>{w}</div>
        ))}
      </div>

      {/* days */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {cells.map((day, i) => (
          <DayCell key={i} day={day} view={view} selectedDate={selectedDate} onPick={onPick} />
        ))}
      </div>
    </div>
  );
}

// ─── TimeChip ────────────────────────────────────────────────────

function TimeChip({ label, disabled, selected, onClick }: {
  label: string;
  disabled?: boolean;
  selected?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-time-chip={disabled ? undefined : label}
      title={disabled ? 'This time slot is already booked' : undefined}
      style={{
        height: 42,
        borderRadius: 11,
        border: `0.5px solid ${selected ? 'transparent' : 'var(--f-hairline, rgba(28,18,12,0.10))'}`,
        background: selected
          ? 'var(--f-sel-bg, #1c160f)'
          : disabled
            ? 'transparent'
            : 'var(--f-surface, #FBF8F2)',
        color: selected
          ? 'var(--f-sel-fg, #F5EDE0)'
          : disabled
            ? 'var(--f-ink-faint, rgba(28,18,12,0.40))'
            : 'var(--f-ink, #1c160f)',
        fontFamily: 'inherit',
        fontSize: 13.5,
        fontVariantNumeric: 'tabular-nums',
        letterSpacing: '-0.005em',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        textDecoration: disabled ? 'line-through' : 'none',
        textDecorationColor: 'rgba(28,18,12,0.25)',
        transition: `background 240ms ${FLOW_SPRING}, color 240ms ${FLOW_SPRING}, border-color 200ms ease`,
        WebkitTapHighlightColor: 'transparent',
        boxShadow: selected ? '0 6px 14px rgba(28,18,12,0.18)' : 'none',
        padding: 0,
      }}
    >
      {label.replace(/^0(?=\d:)/, '')}
    </button>
  );
}

// ─── Component ───────────────────────────────────────────────────

export default function DateTimeSelectionStep({
  serviceDuration,
  selectedDateTime,
  onSelectDateTime,
  businessId,
  providerId,
  onDateChange
}: DateTimeSelectionStepProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    selectedDateTime?.date || undefined
  );
  const [selectedTime, setSelectedTime] = useState<string | null>(
    selectedDateTime?.time || null
  );
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [bookedSlots, setBookedSlots] = useState<string[]>([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [businessTimezone, setBusinessTimezone] = useState<string | undefined>(undefined);

  // Race condition guard: persistent counter across effect runs
  const requestIdRef = useRef(0);

  // Skip sync flag: prevents clearing our own state when we notify parent
  const skipNextSyncRef = useRef(false);

  // Sync internal state when parent prop changes
  useEffect(() => {
    // Skip sync if we just notified parent (avoid clearing our own change)
    if (skipNextSyncRef.current) {
      skipNextSyncRef.current = false;
      return;
    }

    if (!selectedDateTime) {
      setSelectedDate(undefined);
      setSelectedTime(null);
      return;
    }

    setSelectedDate(selectedDateTime.date);
    setSelectedTime(selectedDateTime.time);
  }, [selectedDateTime]);

  // Load availability when date changes
  useEffect(() => {
    // Race condition guard: capture current request ID from shared ref
    const currentRequest = ++requestIdRef.current;

    async function loadAvailability() {
      if (!selectedDate) {
        // Clear slots when date not selected (not an error state)
        setAvailableSlots([]);
        setBookedSlots([]);
        setLoadingAvailability(false);
        return;
      }

      // Resolve businessId: use provided or fetch from providerId
      let resolvedBusinessId = businessId;
      if (!resolvedBusinessId && providerId) {
        resolvedBusinessId = await getBusinessIdFromProviderId(providerId);
      }

      if (!resolvedBusinessId) {
        // No way to determine business context - fail closed
        setAvailableSlots([]);
        setBookedSlots([]);
        setLoadingAvailability(false);
        return;
      }

      setLoadingAvailability(true);

      try {
        // Get business settings and timezone in parallel
        const [settings, bizTimezone] = await Promise.all([
          getBusinessSettings(resolvedBusinessId),
          getBusinessTimezone(resolvedBusinessId)
        ]);

        // Persist business timezone so the label can display it
        const resolvedBizTimezone = bizTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
        setBusinessTimezone(resolvedBizTimezone);

        // Slot generation and the booked-slots query are independent —
        // run them in parallel to cut a full round-trip per date tap
        const [allSlots, booked] = await Promise.all([
          generateTimeSlots(selectedDate, resolvedBusinessId, settings, resolvedBizTimezone),
          fetchBookedSlots({
            businessId: resolvedBusinessId,
            providerId,
            date: selectedDate,
            slotInterval: settings.slot_interval_minutes
          }),
        ]);

        // Discard stale results if dependencies changed during async operations
        if (currentRequest !== requestIdRef.current) return;

        setAvailableSlots(allSlots);
        setBookedSlots(booked);
      } catch (error) {
        console.error('Error loading availability:', error);
        // Discard stale errors if dependencies changed
        if (currentRequest !== requestIdRef.current) return;
        setAvailableSlots([]);
        setBookedSlots([]);
      } finally {
        // Only clear loading if this request is still current
        // (prevents stale requests from clearing loading while newer requests are pending)
        if (currentRequest === requestIdRef.current) {
          setLoadingAvailability(false);
        }
      }
    }

    loadAvailability();
  }, [selectedDate, businessId, providerId]);

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    setSelectedTime(null); // Reset time selection when date changes

    // Notify parent and skip next sync to preserve our state
    skipNextSyncRef.current = true;
    onSelectDateTime(null);
    onDateChange?.(date);
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    if (selectedDate) {
      skipNextSyncRef.current = true;
      onSelectDateTime({ date: selectedDate, time });
    }
  };

  const dayShort = selectedDate
    ? (isToday(selectedDate) ? 'Today' : isTomorrow(selectedDate) ? 'Tomorrow' : format(selectedDate, 'EEE d'))
    : null;

  return (
    <div>
      {/* Month calendar */}
      <div style={{ marginBottom: 26 }}>
        <SectionLabel>Date</SectionLabel>
        <div style={{ marginTop: 12 }}>
          <MonthCalendar selectedDate={selectedDate} onPick={handleDateSelect} />
        </div>
      </div>

      {/* Time grid */}
      <div>
        <SectionLabel>{dayShort ? `Available times · ${dayShort}` : 'Available times'}</SectionLabel>

        {!selectedDate ? (
          <p style={{
            color: 'var(--f-ink-faint, rgba(28,18,12,0.40))',
            padding: '24px 0',
            fontSize: 13.5,
            margin: 0,
          }}>
            Pick a date to see available times
          </p>
        ) : loadingAvailability ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '32px 0',
          }}>
            <div
              className="animate-spin"
              style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                border: '2px solid var(--f-hairline, rgba(28,18,12,0.10))',
                borderTopColor: 'var(--f-ink-soft, rgba(28,18,12,0.62))',
              }}
            />
          </div>
        ) : availableSlots.length > 0 ? (
          <>
            <div style={{
              marginTop: 12,
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 8,
            }}>
              {availableSlots.map((time) => {
                const isBooked = bookedSlots.includes(time);
                return (
                  <TimeChip
                    key={time}
                    label={time}
                    disabled={isBooked}
                    selected={selectedTime === time && !isBooked}
                    onClick={() => !isBooked && handleTimeSelect(time)}
                  />
                );
              })}
            </div>
            <div style={{
              marginTop: 14,
              fontSize: 11.5,
              color: 'var(--f-ink-faint, rgba(28,18,12,0.40))',
              letterSpacing: '-0.005em',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}>
              <span style={{
                display: 'inline-block',
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: 'var(--f-hairline, rgba(28,18,12,0.10))',
              }} />
              Faded times are unavailable
              {businessTimezone && ` · Times in ${businessTimezone.replace(/_/g, ' ')}`}
            </div>
          </>
        ) : (
          <p style={{
            color: 'var(--f-ink-faint, rgba(28,18,12,0.40))',
            padding: '24px 0',
            fontSize: 13.5,
            margin: 0,
          }}>
            No available times for this date
          </p>
        )}
      </div>
    </div>
  );
}
